/**
 * CollegeContext — which college's store the student is viewing.
 *
 * THE CAMPUS IS A PER-SIGN-IN CHOICE, NOT A PERMANENT LABEL
 * ---------------------------------------------------------
 * It used to be fixed to the account forever, so a student who tapped the
 * wrong college on their first day could only escape by signing up again with
 * a different Google address. Now it is asked at every sign-in: to change
 * campus you sign out and sign back in, and you are asked again even with the
 * same email.
 *
 * That is why `confirmed` exists alongside the id. The id alone cannot answer
 * "has this student chosen yet?", because we adopt the account's last campus
 * on a page refresh so the catalogue keeps working. `confirmed` is what says
 * the choice was made in THIS session, and it is the flag CollegePicker
 * blocks on. Both are cleared on sign-out and at the start of every sign-in.
 */

/* eslint-disable react-refresh/only-export-components */

import { createContext, useContext, useState, useCallback } from 'react';
import { useColleges } from '@/hooks/useStorefront';

const CollegeContext = createContext(null);
const STORAGE_KEY = 'rocket07_college';
const CONFIRMED_KEY = 'rocket07_college_confirmed';

export function CollegeProvider({ children }) {
  const { data: colleges = [], isLoading } = useColleges();
  const [collegeId, setCollegeId] = useState(() => localStorage.getItem(STORAGE_KEY) || '');
  const [isConfirmed, setIsConfirmed] = useState(() => localStorage.getItem(CONFIRMED_KEY) === '1');

  /**
   * @param {string} id
   * @param {{ confirmed?: boolean }} [options] `confirmed` only when the
   *   student picked it themselves — adopting the account's campus after a
   *   refresh must not count as choosing, or the picker would be skipped.
   */
  const setCollege = useCallback((id, { confirmed = false } = {}) => {
    setCollegeId(id ?? '');
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);

    if (confirmed) {
      setIsConfirmed(true);
      localStorage.setItem(CONFIRMED_KEY, '1');
    }
  }, []);

  /** Forget the campus entirely, so the next sign-in has to ask again. */
  const resetCollegeChoice = useCallback(() => {
    setCollegeId('');
    setIsConfirmed(false);
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(CONFIRMED_KEY);
  }, []);

  // The chosen college must still exist & be active in the fetched list.
  const college = colleges.find((c) => String(c._id) === String(collegeId)) ?? null;

  const value = {
    collegeId: college?._id ?? '',
    college,
    colleges,
    isLoadingColleges: isLoading,
    isCollegeConfirmed: isConfirmed,
    setCollege,
    resetCollegeChoice,
  };

  return <CollegeContext.Provider value={value}>{children}</CollegeContext.Provider>;
}

export function useCollege() {
  const context = useContext(CollegeContext);
  if (!context) throw new Error('useCollege must be used inside a <CollegeProvider>');
  return context;
}

export default CollegeContext;
